'use client'

import { formatDate, formatValue } from '@/helpers'
import { CategoryScale, Chart as ChartJS, Filler, Legend, LinearScale, LineElement, PointElement, Title, Tooltip } from 'chart.js'
import { Line } from 'react-chartjs-2'
import styles from './Fundamentals.module.css'

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Filler,
  Legend
)

interface ChartDataPoint {
  c: string // code
  v: number // value
  r: string // date
}

interface ChartSectionProps {
  data: ChartDataPoint[]
  seriesCode: string
  title: string
}

export function ChartSection({ data, seriesCode, title }: ChartSectionProps) {
  // Prepare the chart data
  const chartLabels = data.map(point => formatDate(point.r))
  const chartValues = data.map(point => point.v)

  const chartData = {
    labels: chartLabels,
    datasets: [
      {
        fill: true,
        label: title,
        data: chartValues,
        borderColor: 'rgb(59, 130, 246)',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        borderWidth: 1,
        // Remove the dots on the line
        pointRadius: 0,
        pointHoverRadius: 4,
        pointBackgroundColor: 'rgb(59, 130, 246)',
        tension: 0.4,
      },
    ],
  }

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    // Decrease padding
    layout: {
      padding: {
        left: 0,
        right: 0,
      }
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: '#fff',
        bodyColor: '#fff',
        // Simplified tooltip showing only date and Indian rupee value
        callbacks: {
          label: function (context: any) {
            let value = context.raw;
            // Format as Indian rupee value with appropriate abbreviation
            return '₹' + formatValue(value, true, 0);
          }
        }
      },
    },
    scales: {
      y: {
        beginAtZero: false,
        border: {
          display: false, // Remove y-axis line
        },
        // Format Y-axis as Indian rupees without decimals
        ticks: {
          maxTicksLimit: 5, // Show maximum 5 ticks on Y-axis
          color: 'rgb(100, 116, 139)', // Light blue color
          callback: function (value: any) {
            // Use Indian numbering system (K, L, Cr)
            // return formatValue(value, true, 0);
            const absValue = Math.abs(value);
            if (absValue >= 1e12) return (value / 1e12) + ' LCr';
            if (absValue >= 1e10) return (value / 1e10) + ' kCr';
            if (absValue >= 1e7) return (value / 1e7) + ' Cr';
            if (absValue >= 1e5) return (value / 1e5) + ' L';
            if (absValue >= 1e3) return (value / 1e3) + ' K';
            return value;
          }
        },
        grid: {
          color: 'rgba(0, 0, 0, 0.05)',
        }
      },
      x: {
        border: {
          display: false, // Remove y-axis line
        },
        grid: {
          display: false,
        },
        ticks: {
          maxTicksLimit: 5, // Show maximum 5 ticks on X-axis
          maxRotation: 0, // Horizontal labels
          minRotation: 0, // Keep labels horizontal
          color: 'rgb(100, 116, 139)', // Light blue color
          autoSkip: true, // Allow auto skipping of labels
          autoSkipPadding: 15 // Ensure labels don't overlap
        }
      }
    },
    elements: {
      point: {
        radius: 0, // No points on the line
        hoverRadius: 4, // Show points only on hover
      },
    },
    interaction: {
      mode: 'index' as const,
      intersect: false,
    },
  }

  return (
    <div className={styles.chart}>
      <div className={styles.chartFooter}>
        <div className={styles.latestValue}>
          Latest reported: ₹{formatValue(chartValues[chartValues.length - 1], true, 0)}
        </div>
      </div>
      <div className={styles.chartWrapper}>
        <Line data={chartData} options={options} height={200} />
      </div>
    </div>
  )
}